import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SharedService } from 'src/app/_shared/shared.service';
import { RepService, INotification } from '../services/rep.service';
import { Reservation } from '../models/reservation';

@Component({
  selector: 'app-rep-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class RepDashboardComponent implements OnInit {
  upcomingReservations: Reservation[] = [];
  todayReservations: Reservation[] = [];
  notifications: INotification[] = [];
  dataReady = false;
  userName = '';

  // Statistics
  totalReservations = 0;
  completedReservations = 0;
  upcomingCount = 0;

  constructor(
    private repService: RepService,
    private shared: SharedService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.userName = this.shared.login?.fullname || '';
    this.loadData();
  }

  async loadData(): Promise<void> {
    await Promise.all([
      this.loadReservations(),
      this.loadNotifications()
    ]);
  }

  async loadReservations(): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await this.repService.getReservations({
        date_from: today,
        paginator: { limit: 10, orderBy: 'date_from', orderDir: 'ASC' }
      });

      if (result?.rows) {
        const now = new Date();
        this.todayReservations = Reservation.fromArray(
          result.rows.filter(r => {
            const resDate = new Date(r.date_from);
            return resDate.toDateString() === now.toDateString();
          })
        );
        this.upcomingReservations = Reservation.fromArray(
          result.rows.filter(r => {
            const resDate = new Date(r.date_from);
            return resDate > now;
          }).slice(0, 5)
        );

        // Update statistics
        this.totalReservations = result.count || result.rows.length;
        this.completedReservations = result.rows.filter(r => r.status === 'COMPLETED').length;
        this.upcomingCount = this.upcomingReservations.length;
      }
      this.dataReady = true;
    } catch (error) {
      console.error('Error loading reservations:', error);
    }
  }

  async loadNotifications(): Promise<void> {
    try {
      const result = await this.repService.getNotifications();
      if (result?.rows) {
        // Show only unread notifications
        this.notifications = result.rows.filter(n => !n.is_read).slice(0, 5);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }

  async dismissNotification(notification: INotification, event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await this.repService.markNotificationRead(notification.id_notification);
      this.notifications = this.notifications.filter(n => n.id_notification !== notification.id_notification);
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  }

  async dismissAllNotifications(): Promise<void> {
    try {
      await this.repService.markAllNotificationsRead();
      this.notifications = [];
    } catch (error) {
      console.error('Error dismissing notifications:', error);
    }
  }

  onNotificationClick(notification: INotification): void {
    if (notification.id_reservation) {
      this.viewReservation(notification.id_reservation);
    }
    // Mark as read
    this.repService.markNotificationRead(notification.id_notification);
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'CANCELLED': return 'fa-times-circle text-danger';
      case 'UPDATED': return 'fa-edit text-warning';
      case 'CONFIRMED': return 'fa-check-circle text-success';
      default: return 'fa-bell text-info';
    }
  }

  createReservation(): void {
    this.router.navigate(['/reservations/new']);
  }

  viewReservations(): void {
    this.router.navigate(['/reservations']);
  }

  viewReservation(id: number): void {
    this.router.navigate(['/reservations', id]);
  }

  formatDateTime(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatTime(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
